import { ModalGeneric } from "@/components/utils/ModalGeneric";
import { filtros, FiltrosProps } from "../Mock";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { isFuture, isPast, isToday } from "date-fns";
import { Circle } from "lucide-react";
import ImageGeneric from "@/assets/images/lg.png";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { toast } from "react-toastify";
import ModalSubtarefas from "./sections/ModalSubtarefas";
import { IconEdit } from "@/assets/images/Icons";
import { IconDelete } from "@/pages/UserGoOn/Desks/ResumoDesk/Icons";
import { confirmAlert } from "react-confirm-alert";
import { useLayout } from "@/contexts/UseLayout";
import FormComentario from "./sections/FormComentario";
import GraficoPie from "./sections/GraficoPie";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { buildTaskStatusPayload } from "@/utils/taskStatusPayload";

interface ModalExpansaoProps {
  filtroAtivo: string;
  close: () => void;
  setFiltroAtivo: (value: any) => void;
  setPayload: (value: any) => void;
  dataValue: any;
  isPending?: any;
  checkedTask: (value: any) => void;
  setTaskSelected: (value: any) => void;
  openEdit: (value: any) => void;
}

export default function ModalExpansao({
  close,
  setFiltroAtivo,
  setPayload,
  filtroAtivo,
  dataValue,
  checkedTask,
  setTaskSelected,
  isPending,
  openEdit,
}: ModalExpansaoProps) {
  const { t } = useTranslation();
  const { userLogged } = useLayout();
  const queryClient = useQueryClient();
  const swiperRef = useRef<any>(null);
  const comentariosEndRef = useRef<HTMLDivElement | null>(null);
  const [verMais, setVerMais] = useState<any>();
  const [subTarefas, setSubTarefas] = useState<any>();
  const [idSubTarefas, setIdSubTarefas] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);

  const { data: dataSubTarefas } = useQuery({
    queryKey: ["subTarefas", idSubTarefas],
    queryFn: async () => await ServiceResumoDesk.subTask(idSubTarefas),
    retry: 3,
    enabled: !!idSubTarefas,
    staleTime: 1000 * 60 * 5,
  });

  const { isPending: isPendingComentarios, data: dataComentarios } = useQuery({
    queryKey: [`comentarios-tarefas`, idSubTarefas],
    queryFn: async () =>
      await ServiceResumoDesk.getComentarios(idSubTarefas, "tarefa"),
    retry: 3,
    enabled: !!idSubTarefas,
    staleTime: 1000 * 60 * 5,
  });

  const comentariosTarefa = useMemo(() => {
    const comentarios = Array.isArray(dataComentarios?.data)
      ? dataComentarios.data
      : [];

    return [...comentarios].sort((a: any, b: any) => {
      const dateA = new Date(getComentarioCreatedAt(a)).getTime();
      const dateB = new Date(getComentarioCreatedAt(b)).getTime();
      return dateA - dateB;
    });
  }, [dataComentarios?.data]);

  useEffect(() => {
    if (!verMais || isPendingComentarios) return;

    const timer = setTimeout(() => {
      comentariosEndRef.current?.scrollIntoView({
        behavior: "auto",
        block: "end",
      });
    }, 120);

    return () => clearTimeout(timer);
  }, [
    comentariosTarefa.length,
    idSubTarefas,
    isPendingComentarios,
    verMais,
  ]);

  function closeModal() {
    close();
  }

  function handleFiltroClick(value: string) {
    setFiltroAtivo(value);

    if (value === "hoje" || value === "amanha" || value === "semana") {
      setPayload({ period: value });
    } else if (value === "pendentes") {
      setPayload({ pendentes: true });
    } else if (value === "emAndamento") {
      setPayload({ emAndamento: true });
    } else if (value === "concluidas") {
      setPayload({ concluidas: true });
    } else if (value === "reembolso") {
      setPayload({ typeTarefa: 2 });
    }
  }

  function handleVerMais(itemId: string, index: number) {
    setVerMais(itemId);

    setTimeout(() => {
      swiperRef.current?.update();
      swiperRef.current?.slideTo(index, 800, {
        // Comportamentos opcionais:
        smooth: true,
        // force: true
      });
    }, 100);
  }

  function colorDate(data: any) {
    if (isToday(data)) {
      return "text-[#FFA100]";
    } else if (isPast(data)) {
      return "text-red-500";
    } else if (isFuture(data)) {
      return "text-azul-400";
    }
  }

  async function handleSetInProgress(task: any) {
    try {
      const status = 3; // Status "Em andamento"
      const payload = buildTaskStatusPayload(status, task);
      const res = await ServiceResumoDesk.editTasks(payload, task.id);
      if (res.status === 200) {
        toast.success(t("tasks.view.in_progress_success"));
        // getTasks();
        setTaskSelected(null);
        await queryClient.invalidateQueries({ queryKey: ["tarefas"] });
      }
    } catch (error) {
      toast.error(t("tasks.messages.editError"));
    }
  }

  async function checkedSubTask(id: any, status: any) {
    try {
      const payload = { status };
      const res = await ServiceResumoDesk.subTaskedit(payload, id);
      if (res.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["subTarefas"] });
      }
    } catch (error) {
      toast.error(t("tasks.messages.editError"));
    }
  }

  async function deleteSubTask(id: any) {
    confirmAlert({
      title: t("common:confirmation"),
      message: t("tasks.subtasks.delete_confirm"),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            try {
              const res = await ServiceResumoDesk.subTaskdel(id);
              if (res.status === 200) {
                queryClient.invalidateQueries({ queryKey: ["subTarefas"] });
              }
            } catch (error) {
              console.error("Erro:", error);
              toast.error("Erro ao Deletar");
            }
          },
        },
        {
          label: t("common:cancel"),
          onClick: () => {},
        },
      ],
    });
  }

  function statusName(statusId: any) {
    const statusConfig: any = {
      1: { name: "Em Análise" },
      2: { name: "Analisado" },
      3: { name: "Fazendo" },
      4: { name: "Aprovação Interna" },
      5: { name: "Ajuste" },
      6: { name: "Aprovação Externa" },
      7: { name: "Aprovado" },
      8: { name: "Em Entrega" },
      9: { name: "Entregue" },
    };
    return !!statusId ? statusConfig[statusId] : null;
  }

  function getPriorityLabel(priority: any) {
    const statusConfig: any = {
      1: { label: "Alta", color: "bg-red-500" },
      2: { label: "Média", color: "bg-yellow-400" },
      3: { label: "Baixa", color: "bg-azul-400" },
    };
    return !!priority
      ? statusConfig[priority]
      : { label: t("tasks.labels.noObjective"), color: "bg-gray-400" };
  }

  function makeLinksClickable(text: string) {
    if (!text) return `<p>${t("tasks.labels.noObjective")}</p>`;

    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Substitui URLs por links clicáveis
    return text.replace(
      urlRegex,
      (url) =>
        `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-azul-600 underline">${url}</a>`,
    );
  }

  function getComentarioCreatedAt(comentario: any) {
    return (
      comentario?.createdAt ??
      comentario?.created_at ??
      comentario?.updatedAt ??
      comentario?.updated_at ??
      new Date().toISOString()
    );
  }

  function formatComentarioDate(date: string) {
    return moment(date).format("DD/MM/YYYY");
  }

  function formatComentarioTime(date: string) {
    return moment(date).format("HH:mm");
  }

  function shouldShowComentarioDate(
    comentario: any,
    index: number,
    comentarios: any[],
  ) {
    if (index === 0) return true;

    return !moment(getComentarioCreatedAt(comentario)).isSame(
      getComentarioCreatedAt(comentarios[index - 1]),
      "day",
    );
  }

  async function deleteDesk(id: any) {
    confirmAlert({
      title: t("common:confirmation"),
      message: t("tasks.view.delete_confirm"),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            try {
              const res = await ServiceResumoDesk.delTasks(id);
              if (res.status === 200) {
                await queryClient.invalidateQueries({ queryKey: ["tarefas"] });
              }
            } catch (error) {
              console.error("Erro:", error);
              toast.error("Erro ao editar tarefa");
            }
          },
        },
        {
          label: t("common:cancel"),
          onClick: () => {},
        },
      ],
    });
  }

  return (
    <>
      <ModalGeneric
        onClose={closeModal}
        size="w-full max-w-[95%] max-h-[90vh] fle"
        styleRounded="rounded-[51px]"
      >
        <div className="bg-[#153ECE]/5 rounded-[27px] p-1 inline-flex gap-2">
          {filtros(t)?.map((item: FiltrosProps, index: number) => (
            <button
              key={index}
              type="button"
              className={`text-sm transition-all hover:bg-white hover:text-[#153ECE] ${filtroAtivo === item?.value ? "bg-white text-[#153ECE]" : "text-black"} rounded-[25px] px-4 py-1 last:hidden lg:last:flex`}
              onClick={() => {
                handleFiltroClick(item?.value);
                queryClient.invalidateQueries({ queryKey: ["tarefas"] });
              }}
            >
              {item?.label}
            </button>
          ))}
        </div>
        <div className="flex gap-4 w-full">
          <div className="w-9/12">
            {dataValue?.length > 0 && !isPending && (
              <div className="bg-[#F2F2F2] w-full rounded-[51px] min-h-[60dvh] py-6 px-5 mt-6">
                <Swiper
                  onSwiper={(swiper) => (swiperRef.current = swiper)}
                  className={`mySwiper`}
                  direction="horizontal"
                  pagination={true}
                  slidesPerView="auto"
                  spaceBetween={10}
                  autoHeight={false}
                  observer={true}
                  observeParents={true}
                  modules={[Navigation, Pagination, Scrollbar, A11y]}
                >
                  {dataValue?.map((item: any, index: number) => (
                    <SwiperSlide key={index} className={`!w-auto`}>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={item?.id}
                          initial={false}
                          animate={
                            verMais === item?.id
                              ? { height: 500, width: 720 }
                              : { height: 160, width: 320 }
                          }
                          transition={{ duration: 0.5 }}
                          onAnimationComplete={() => {
                            swiperRef.current?.update();
                          }}
                          className="bg-white rounded-3xl py-2 px-4 flex flex-col relative"
                        >
                          <div className="flex items-center justify-end gap-4">
                            <div
                              className={`text-white text-xs px-2 py-1 rounded-full inline-flex items-center justify-center ${getPriorityLabel(item?.priority)?.color}`}
                            >
                              <span>
                                {getPriorityLabel(item?.priority)?.label}
                              </span>
                            </div>
                            <p
                              className={`text-end ${colorDate(item?.date)} text-[10px]`}
                            >
                              <strong>
                                {moment
                                  .utc(item?.date)
                                  .format("DD/MM/YYYY - HH:mm")}
                              </strong>
                            </p>
                          </div>
                          <div className="flex flex-1 gap-6 w-full min-h-0">
                            <div
                              className={`flex flex-col flex-1 gap-2 min-h-0 ${verMais === item?.id ? "w-1/2" : "w-full"}`}
                            >
                              <div>
                                <div className="flex gap-2 items-start">
                                  <button
                                    type="button"
                                    onClick={() => checkedTask(item)}
                                    className="pt-1"
                                  >
                                    {item?.status === 1 && (
                                      <Circle color="#153DCE" size={16} />
                                    )}
                                    {item?.status === 2 && (
                                      <Circle
                                        size={16}
                                        color="#153DCE"
                                        fill={`${item?.status === 2 && "#153DCE "}`}
                                      />
                                    )}
                                    {item?.status === 3 && <Circle size={16} />}
                                  </button>
                                  <p
                                    className={`text-[15px] text-[#1E1E1E] ${verMais === item?.id ? "" : "line-clamp-1"} ${item?.status === 2 && "line-through"}`}
                                  >
                                    <strong>{item?.title}</strong>
                                  </p>
                                </div>
                                <div className="overflow-y-auto max-h-24">
                                  <div
                                    className={`text-xs linkColor text-black/40 pl-6 break-all pr-1 ${verMais === item?.id ? "" : "line-clamp-2"}`}
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        makeLinksClickable(item?.description) ||
                                        `<p>${t("tasks.labels.noObjective")}</p>`,
                                    }}
                                  />
                                </div>
                              </div>
                              {verMais === item?.id &&
                                !!item?.cliente?.name && (
                                  <div>
                                    <p className="text-[10px] text-black/40">
                                      Cliente da Desk
                                    </p>
                                    <p className="font-medium text-xs">
                                      {item?.cliente?.name}
                                    </p>
                                  </div>
                                )}
                              {verMais === item?.id && !!item?.agency?.name && (
                                <div>
                                  <p className="text-[10px] text-black/40">
                                    Agência da Desk
                                  </p>
                                  <p className="font-medium text-xs">
                                    {item?.agency?.name}
                                  </p>
                                </div>
                              )}
                              <div>
                                {verMais === item?.id && (
                                  <p className="text-[10px] text-black/40">
                                    Organizador
                                  </p>
                                )}
                                <div className="inline-flex items-center border border-black/30 p-1 rounded-full gap-2">
                                  <img
                                    src={item?.user?.img ?? ImageGeneric}
                                    alt={item?.user?.name}
                                    className="w-5 h-5 object-cover rounded-full"
                                  />
                                  <p className="font-medium text-xs">
                                    {item?.user?.name} {item?.user?.last_name}
                                  </p>
                                </div>
                              </div>
                              {verMais === item?.id && (
                                <div>
                                  <p className="text-[10px] text-black/40">
                                    Responsável
                                  </p>
                                  <div className="inline-flex items-center border border-black/30 p-1 rounded-full gap-2">
                                    <img
                                      src={
                                        item?.responsavel?.img ?? ImageGeneric
                                      }
                                      alt={item?.responsavel?.name}
                                      className="w-5 h-5 object-cover rounded-full"
                                    />
                                    <p className="font-medium text-xs">
                                      {item?.responsavel?.name}{" "}
                                      {item?.responsavel?.last_name}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {verMais === item?.id && (
                                <div className="text-[10px] flex items-center gap-8">
                                  <p className="text-black/40">Subtarefas</p>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSubTarefas(item);
                                    }}
                                    className="text-azul-400"
                                  >
                                    <span>+ Nova tarefa</span>
                                  </button>
                                </div>
                              )}
                              {verMais === item?.id && (
                                <div className="space-y-1 mt-2 overflow-y-auto ">
                                  {dataSubTarefas?.data?.data?.map(
                                    (itemSubTarefa: any) => (
                                      <div className="" key={itemSubTarefa?.id}>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() =>
                                              checkedSubTask(
                                                itemSubTarefa?.id,
                                                itemSubTarefa?.status == 1
                                                  ? 2
                                                  : 1,
                                              )
                                            }
                                            type="button"
                                          >
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 11 11"
                                              fill={
                                                itemSubTarefa?.status == 1
                                                  ? "none"
                                                  : "#153DCE"
                                              }
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <circle
                                                cx="5.5"
                                                cy="5.5"
                                                r="5"
                                                stroke={
                                                  itemSubTarefa.status == 1
                                                    ? "black"
                                                    : "#153DCE"
                                                }
                                              />
                                            </svg>
                                          </button>
                                          <p
                                            className={`text-[11px] text-black ${itemSubTarefa?.status == 1 ? "" : "line-through"}`}
                                          >
                                            {itemSubTarefa?.title}
                                          </p>
                                        </div>
                                        <p
                                          className={`text-xs text-gray-600 pl-5`}
                                        >
                                          {itemSubTarefa?.description}
                                        </p>
                                        <div className="flex items-center gap-2 pl-5">
                                          {/* <span>{itemSubTarefa?.responsavel}</span> */}
                                          <div className="flex items-center justify-end gap-2">
                                            <button
                                              type="button"
                                              className="transition-all hover:text-azul-400"
                                              onClick={() => {
                                                setSubTarefas(itemSubTarefa);
                                                setIsEdit(true);
                                              }}
                                            >
                                              <IconEdit />
                                            </button>
                                            <button
                                              type="button"
                                              className="transition-all hover:text-red-500"
                                              onClick={() =>
                                                deleteSubTask(itemSubTarefa?.id)
                                              }
                                            >
                                              <IconDelete />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                              <div className="flex items-center justify-end flex-col flex-1">
                                {verMais === item?.id && (
                                  <div className="w-full flex justify-between items-center gap-3">
                                    {!!statusName(item?.status)?.name && (
                                      <div className="text-cinza-500 text-xs rounded-3xl px-4 py-2 border border-black/40 flex items-center gap-1">
                                        <div className="rounded-full w-3 h-3 bg-cinza-400"></div>
                                        <span>
                                          {statusName(item?.status)?.name}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex items-center justify-end gap-3 bg-azul-400 w-fit rounded-xl px-3 py-1">
                                      <button
                                        type="button"
                                        className="text-white"
                                        onClick={() => openEdit(item)}
                                      >
                                        <IconEdit />
                                      </button>
                                      <button
                                        type="button"
                                        className="text-white hover:text-red-500 duration-300"
                                        onClick={() => deleteDesk(item?.id)}
                                      >
                                        <IconDelete />
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        handleSetInProgress(item);
                                      }}
                                      className="text-xs rounded-3xl px-4 py-2 bg-[#0AF00A4D]/30 text-[#00C500] inline-flex items-center justify-center"
                                    >
                                      Iniciar Tarefa
                                    </button>
                                  </div>
                                )}
                                {verMais === item?.id ? (
                                  <button
                                    type="button"
                                    className="text-[10px] text-center inline-flex items-center gap-1 text-black/30 hover:text-azul-400 transition-all"
                                    onClick={() => {
                                      setVerMais(null);
                                    }}
                                  >
                                    <span>Ver menos</span>
                                    <svg
                                      className={``}
                                      width="8"
                                      height="5"
                                      viewBox="0 0 8 5"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M0.998281 1.02562L4.02344 4.05078L7.04859 1.02562"
                                        stroke="black"
                                        strokeOpacity="0.3"
                                      />
                                    </svg>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="text-[10px] text-center inline-flex items-center gap-1 text-black/30 hover:text-azul-400 transition-all"
                                    onClick={() => {
                                      handleVerMais(item?.id, index);
                                      setIdSubTarefas(item?.id);
                                    }}
                                  >
                                    <span>Ver mais</span>
                                    <svg
                                      className={``}
                                      width="8"
                                      height="5"
                                      viewBox="0 0 8 5"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M0.998281 1.02562L4.02344 4.05078L7.04859 1.02562"
                                        stroke="black"
                                        strokeOpacity="0.3"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div
                              className={`${verMais === item?.id ? "w-1/2 flex flex-col flex-1 min-h-0 overflow-hidden gap-4" : "hidden"}`}
                            >
                              <div className="flex items-center gap-2">
                                <svg
                                  width="18"
                                  height="16"
                                  viewBox="0 0 18 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M6.63407 11.4915C9.43987 11.3382 11.667 9.05014 11.667 6.24995C11.667 3.35048 9.2791 1 6.33349 1C3.38789 1 1 3.35048 1 6.24995C1 7.28306 1.30306 8.24642 1.82659 9.05864L1.44967 10.1717L1.44907 10.1733C1.3047 10.5997 1.23248 10.8129 1.28391 10.9549C1.32871 11.0785 1.42818 11.176 1.55384 11.2202C1.69755 11.2706 1.91269 11.2 2.34288 11.0589L2.349 11.057L3.48011 10.686C4.30526 11.2013 5.284 11.4997 6.33355 11.4997C6.4344 11.4997 6.5346 11.497 6.63407 11.4915ZM6.63407 11.4915V11.4915ZM6.63407 11.4915C7.36381 13.535 9.34167 15 11.6671 15C12.7167 15 13.6952 14.7013 14.5203 14.186L15.6511 14.557L15.6533 14.5574C16.0864 14.6995 16.3034 14.7707 16.4476 14.7201C16.5733 14.676 16.6715 14.5785 16.7163 14.4548C16.7678 14.3127 16.6958 14.0992 16.551 13.6717L16.1741 12.5586L16.3003 12.3527C16.7461 11.5856 17 10.6969 17 9.74992C17 6.85046 14.6126 4.49997 11.667 4.49997L11.4673 4.50359L11.3666 4.50843"
                                    stroke="black"
                                    strokeOpacity="0.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <p className="text-[15px] text-[#1E1E1E]">
                                  <strong>Notas</strong>
                                </p>
                              </div>
                              <div className="flex flex-col flex-1 min-h-0 bg-[#F1F1F1] rounded-[14px] relative">
                                <div className="overflow-y-auto">
                                  <div className="text-sm leading-relaxed break-words whitespace-normal p-4 flex flex-col gap-4">
                                    {!isPendingComentarios ? (
                                      comentariosTarefa?.map(
                                        (
                                          itemComentarios: any,
                                          indexComentarios: number,
                                        ) => {
                                          const isOwn =
                                            userLogged?.data?.id ===
                                            itemComentarios?.user_id;
                                          const createdAt =
                                            getComentarioCreatedAt(
                                              itemComentarios,
                                            );
                                          const showDate =
                                            shouldShowComentarioDate(
                                              itemComentarios,
                                              indexComentarios,
                                              comentariosTarefa,
                                            );

                                          return (
                                            <div
                                              key={
                                                itemComentarios?.id ??
                                                indexComentarios
                                              }
                                            >
                                              {showDate && (
                                                <div className="flex justify-center py-1">
                                                  <span className="rounded-full bg-[#E1E3E8] px-3 py-1 text-[11px] font-medium text-cinza-600">
                                                    {formatComentarioDate(
                                                      createdAt,
                                                    )}
                                                  </span>
                                                </div>
                                              )}

                                              <div
                                                className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                                              >
                                                <div className="max-w-[85%]">
                                                  {!isOwn && (
                                                    <p className="mb-1 px-1 text-[11px] text-cinza-500">
                                                      {
                                                        itemComentarios?.user
                                                          ?.name
                                                      }{" "}
                                                      {
                                                        itemComentarios?.user
                                                          ?.last_name
                                                      }{" "}
                                                      -{" "}
                                                      {formatComentarioTime(
                                                        createdAt,
                                                      )}
                                                    </p>
                                                  )}

                                                  <div
                                                    className={`${
                                                      isOwn
                                                        ? "bg-[#153ECE] rounded-b-[20px] rounded-tl-[20px] text-white"
                                                        : "bg-white rounded-b-[20px] rounded-tr-[20px] text-[#1E1E1E]"
                                                    } relative p-4 pb-6 text-[10px]`}
                                                  >
                                                    {!isOwn && (
                                                      <img
                                                        src={
                                                          itemComentarios?.user
                                                            ?.url_img ??
                                                          ImageGeneric
                                                        }
                                                        alt={
                                                          itemComentarios?.user
                                                            ?.name
                                                        }
                                                        className="absolute -left-2 -top-2 h-6 w-6 rounded-full bg-cinza-400 object-cover"
                                                      />
                                                    )}
                                                    <p className="break-words">
                                                      {
                                                        itemComentarios?.content
                                                      }
                                                    </p>
                                                    <span
                                                      className={`absolute bottom-2 right-4 text-[10px] leading-none ${
                                                        isOwn
                                                          ? "text-white/70"
                                                          : "text-cinza-500"
                                                      }`}
                                                    >
                                                      {formatComentarioTime(
                                                        createdAt,
                                                      )}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        },
                                      )
                                    ) : (
                                      <div className="flex flex-col gap-4 animate-pulse w-full">
                                        <div className=" bg-cinza-200 rounded-2xl py-4 w-full"></div>
                                        <div className=" bg-cinza-200 rounded-2xl py-4 w-full"></div>
                                      </div>
                                    )}
                                    <div ref={comentariosEndRef} />
                                  </div>
                                </div>
                                <div className="sticky flex flex-1 justify-end items-end">
                                  <FormComentario
                                    type="tarefa"
                                    parentId={idSubTarefas}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
            {!!isPending && (
              <div className="bg-[#F2F2F2] w-full rounded-[51px] min-h-[60dvh] p-6 mt-6 flex">
                <div className="flex flex-wrap gap-6">
                  {[...Array(3)].map((_, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-3xl py-4 px-6 flex flex-col w-[320px] h-[160px] animate-pulse shadow"
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-4 bg-gray-200 rounded-full"></div>
                        <div className="w-24 h-3 bg-gray-200 rounded-full"></div>
                      </div>

                      {/* Título */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                        <div className="w-2/3 h-4 bg-gray-200 rounded"></div>
                      </div>

                      {/* Descrição */}
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="w-full h-3 bg-gray-200 rounded"></div>
                        <div className="w-5/6 h-3 bg-gray-200 rounded"></div>
                      </div>

                      {/* Rodapé */}
                      <div className="flex items-center justify-end mt-4">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
                          <div className="w-24 h-3 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dataValue?.length == 0 && !isPending && (
              <div className="bg-[#F2F2F2] w-full rounded-[51px] min-h-[60dvh] p-6 mt-6 flex justify-center items-center">
                <p className="text-base text-cinza-800 font-medium">
                  {t("tasks.no_tasks_found")}
                </p>
              </div>
            )}
          </div>
          <div className="w-3/12">
            <GraficoPie tarefasValue={dataValue} />
          </div>
        </div>
      </ModalGeneric>
      {!!subTarefas && (
        <ModalSubtarefas
          setSubTarefas={setSubTarefas}
          idTarefa={idSubTarefas}
          setIsEdit={setIsEdit}
          isEdit={isEdit}
          subTarefas={subTarefas}
        />
      )}
    </>
  );
}
